import { isDefined } from "@clipboard-health/util-ts";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useGetFacilityProfile } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useCreateWorkplaceAgreement } from "@src/appV2/WorkplaceAgreement/api/useCreateWorkplaceAgreement";
import { useGetWorkplaceAgreements } from "@src/appV2/WorkplaceAgreement/api/useGetWorkplaceAgreements";
import { WorkplaceAgreementPerUserDialog } from "@src/appV2/WorkplaceAgreement/WorkplaceAgreementPerUserDialog";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";

import { WorkplaceAgreementDialogView } from "./WorkplaceAgreementDialogView";

/**
 * Dispatches on the `REQUIRE_WORKPLACE_AGREEMENT` flag variation, which LaunchDarkly targets per
 * workplace (`workplaceId` / `parentFacilityId`). Each variation is its own component so its hooks
 * and API calls run only when that variation is active.
 */
export function WorkplaceAgreementDialog() {
  const { variation } = useCbhFlag(CbhFeatureFlag.REQUIRE_WORKPLACE_AGREEMENT, {
    defaultValue: {
      variation: "off",
      flexAgreementUrl: "",
      hiringAgreementUrl: "",
    },
  });

  if (variation === "per-user") {
    return <WorkplaceAgreementPerUserDialog />;
  }

  return <WorkplaceAgreementOnDialog />;
}

function WorkplaceAgreementOnDialog() {
  const session = useSession();
  const { admin: isAdmin, profile, user } = session ?? { profile: undefined, user: undefined };
  const workplaceId = profile?.userId;
  const userId = user?._id ?? "";

  const { variation, flexAgreementUrl, hiringAgreementUrl } = useCbhFlag(
    CbhFeatureFlag.REQUIRE_WORKPLACE_AGREEMENT,
    {
      defaultValue: {
        variation: "off",
        flexAgreementUrl: "",
        hiringAgreementUrl: "",
      },
    }
  );

  const requireWorkplaceAgreement =
    isDefined(workplaceId) && !isAdmin && !isEmpty(userId) && variation === "on";
  const {
    data: workplaceAgreements,
    isLoading: isLoadingWorkplaceAgreements,
    isError: isErrorGettingWorkplaceAgreements,
  } = useGetWorkplaceAgreements(workplaceId, undefined, {
    enabled: requireWorkplaceAgreement,
  });
  // Gate the dialog on the first shift post (populated async by the
  // ShiftCreated SQS consumer; shift-create mutations invalidate this query).
  // The string projection is SPACE-delimited (the facility-profile controller splits on " ");
  // a comma would be parsed as a single bogus field name and 400.
  const {
    data: facilityProfile,
    isLoading: isLoadingFacilityProfile,
    isError: isErrorGettingFacilityProfile,
  } = useGetFacilityProfile(workplaceId ?? "", "firstShiftCreatedAt legalEntityName", {
    enabled: requireWorkplaceAgreement,
  });
  const { mutateAsync: createWorkplaceAgreement, isLoading: isLoadingCreateWorkplaceAgreement } =
    useCreateWorkplaceAgreement(workplaceId);

  const [visible, setVisible] = useState<boolean>(true);

  // Prefill the editable legal entity name from the facility profile once it loads, until the
  // user edits it. The component stays mounted (returning null) while loading, so seed via effect.
  const [legalEntityName, setLegalEntityName] = useState<string>("");
  const [hasUserEditedLegalEntityName, setHasUserEditedLegalEntityName] = useState<boolean>(false);
  const [isEditingLegalEntityName, setIsEditingLegalEntityName] = useState<boolean>(false);
  useEffect(() => {
    if (!hasUserEditedLegalEntityName && isDefined(facilityProfile?.legalEntityName)) {
      setLegalEntityName(facilityProfile.legalEntityName);
    }
  }, [facilityProfile?.legalEntityName, hasUserEditedLegalEntityName]);

  const trimmedLegalEntityName = legalEntityName.trim();

  // A backfilled legal entity name is treated as authoritative: show it read-only and require a
  // deliberate "Edit" to change it, reducing accidental edits/typos on a signed legal document.
  // When the profile has no value to confirm, fall back to the required input.
  const hasPrefilledLegalEntityName = !isEmpty(facilityProfile?.legalEntityName?.trim() ?? "");
  const isEditingOrEmptyLegalEntityName = isEditingLegalEntityName || !hasPrefilledLegalEntityName;

  const onAgree = async () => {
    try {
      await createWorkplaceAgreement({
        workplaceId,
        acceptedByUserId: userId,
        acceptedAt: new Date(),
        arbitrationAgreementVersionNumber: 1,
        legalEntityName: trimmedLegalEntityName,
      });
      setVisible(false);
    } catch {
      showErrorToast("Failed to accept agreement. Please try again or contact support.");
    }
  };

  if (!requireWorkplaceAgreement) {
    return null;
  }

  if (
    isErrorGettingWorkplaceAgreements ||
    isLoadingWorkplaceAgreements ||
    !isEmpty(workplaceAgreements?.data)
  ) {
    return null;
  }

  if (
    isErrorGettingFacilityProfile ||
    isLoadingFacilityProfile ||
    !isDefined(facilityProfile?.firstShiftCreatedAt)
  ) {
    return null;
  }

  return (
    <WorkplaceAgreementDialogView
      isOpen={visible}
      legalEntityName={legalEntityName}
      hasPrefilledLegalEntityName={hasPrefilledLegalEntityName}
      isEditingLegalEntityName={isEditingLegalEntityName}
      legalEntityNameError={
        isEditingOrEmptyLegalEntityName && isEmpty(trimmedLegalEntityName)
          ? "Legal entity name is required to accept."
          : undefined
      }
      flexAgreementUrl={flexAgreementUrl}
      hiringAgreementUrl={hiringAgreementUrl}
      isSubmitting={isLoadingCreateWorkplaceAgreement}
      onLegalEntityNameChange={(nextLegalEntityName) => {
        setHasUserEditedLegalEntityName(true);
        setLegalEntityName(nextLegalEntityName);
      }}
      onEditLegalEntityName={() => {
        setIsEditingLegalEntityName(true);
      }}
      onAgree={onAgree}
    />
  );
}
